/**
 * ProgressRing - Circular progress indicator
 *
 * Usage:
 *   <ProgressRing
 *     data={{
 *       current: 8241,
 *       target: 10000,
 *       label: 'Daily Steps',
 *       unit: 'steps'
 *     }}
 *   />
 */

import type { BlockProps, ProgressRingData } from './types';
import './blocks.css';

export interface ProgressRingProps extends BlockProps<ProgressRingData> {
  /** Ring size in pixels */
  size?: number;
  /** Stroke width */
  strokeWidth?: number;
  /** Ring color when complete */
  completeColor?: string;
}

export function ProgressRing({
  data,
  title,
  size = 120,
  strokeWidth = 10,
  completeColor = 'var(--color-success)',
  className,
}: ProgressRingProps) {
  const { current: rawCurrent, target: rawTarget, label, unit } = data;

  // Guard against undefined/null values from LLM-generated card data
  const current = rawCurrent ?? 0;
  const target = rawTarget ?? 0;

  // Guard against division by zero
  const safeTarget = target || 1;
  const percentage = Math.min(100, Math.max(0, (current / safeTarget) * 100));
  const isComplete = percentage >= 100;

  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const displayValue = current >= 1000 ? `${(current / 1000).toFixed(1)}k` : current.toString();

  return (
    <div className={`progress-ring ${className || ''}`}>
      {title && (
        <div style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 600,
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 'var(--space-2)',
        }}>
          {title}
        </div>
      )}

      <svg
        className="progress-ring-svg"
        width={size}
        height={size}
      >
        {/* Background circle */}
        <circle
          className="progress-ring-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />

        {/* Progress circle */}
        <circle
          className="progress-ring-progress"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{
            stroke: isComplete ? completeColor : 'var(--color-primary)',
          }}
        />

        {/* Center text */}
        <text
          x={size / 2}
          y={size / 2}
          className="progress-ring-text"
        >
          {displayValue}
        </text>
      </svg>

      {label && (
        <div className="progress-ring-label">
          {label}
          {unit && ` (${current.toLocaleString()} / ${target.toLocaleString()} ${unit})`}
        </div>
      )}

      {!label && (
        <div className="progress-ring-label">
          {percentage.toFixed(0)}% of goal
        </div>
      )}
    </div>
  );
}

export default ProgressRing;
